class UnassociatedFundingLinesModalController {
  constructor(
    $uibModalInstance,
    $http,
    $rootScope,
    $q,
    toastr,
    project,
    lines,
    obligations,
    canManage,
    helpers
  ) {
    'ngInject';

    this.$uibModalInstance = $uibModalInstance;
    this.$http = $http;
    this.$rootScope = $rootScope;
    this.$q = $q;
    this.toastr = toastr;
    this.project = project || {};
    this.lines = (lines || []).slice();
    this.obligations = obligations || [];
    this.canManage = !!canManage;
    this.helpers = helpers || {};
    this.selectedIds = {};
    this.selectedObligationId = null;
    this.saving = false;
  }

  formatMoney(value) {
    return this.helpers.formatMoney ? this.helpers.formatMoney(value) : value;
  }

  currency(value) {
    return this.helpers.currency ? this.helpers.currency(value) : value;
  }

  componentLabel(line) {
    return this.helpers.componentLabel ? this.helpers.componentLabel(line) : (line && line.fundingComponent) || '—';
  }

  getFullyBurdenedRate(line) {
    return this.helpers.getFullyBurdenedRate ? this.helpers.getFullyBurdenedRate(line) : 0;
  }

  getFeeRate(line) {
    return this.helpers.getFeeRate ? this.helpers.getFeeRate(line) : 0;
  }

  getFundedLaborAmount(line) {
    return this.helpers.getFundedLaborAmount ? this.helpers.getFundedLaborAmount(line) : 0;
  }

  getFundedFeeAmount(line) {
    return this.helpers.getFundedFeeAmount ? this.helpers.getFundedFeeAmount(line) : 0;
  }

  getActualHours(line) {
    return this.helpers.getActualHours ? this.helpers.getActualHours(line) : 0;
  }

  getActualLaborCost(line) {
    return this.helpers.getActualLaborCost ? this.helpers.getActualLaborCost(line) : 0;
  }

  obligationOptionLabel(obligation) {
    return this.helpers.obligationOptionLabel
      ? this.helpers.obligationOptionLabel(obligation)
      : (obligation && obligation.clin) || '—';
  }

  obligationsForLine(line) {
    return this.helpers.obligationsForLine
      ? this.helpers.obligationsForLine(line)
      : this.obligations;
  }

  getSelectedLines() {
    return this.lines.filter((line) => this.selectedIds[line.id]);
  }

  selectedCount() {
    return this.getSelectedLines().length;
  }

  allSelected() {
    return this.lines.length > 0 && this.lines.every((line) => this.selectedIds[line.id]);
  }

  toggleSelectAll() {
    const select = !this.allSelected();
    this.lines.forEach((line) => {
      this.selectedIds[line.id] = select;
    });
  }

  compatibleObligations() {
    const selected = this.getSelectedLines();
    if (!selected.length) {
      return this.obligations;
    }

    const components = new Set(
      selected
        .map((line) => line.fundingComponent)
        .filter(Boolean)
    );

    if (components.has('LABOR') && components.has('FEE')) {
      return [];
    }

    if (components.has('LABOR')) {
      return (this.obligations || []).filter((o) => o.costElement === 'LABOR');
    }
    if (components.has('FEE')) {
      return (this.obligations || []).filter((o) => o.costElement === 'FIXED_FEE');
    }
    return this.obligations;
  }

  hasMixedSelection() {
    const selected = this.getSelectedLines();
    const components = new Set(
      selected
        .map((line) => line.fundingComponent)
        .filter(Boolean)
    );
    return components.has('LABOR') && components.has('FEE');
  }

  canAssign() {
    return this.canManage
      && !this.saving
      && this.selectedCount() > 0
      && !!this.selectedObligationId
      && !this.hasMixedSelection()
      && this.compatibleObligations().some(
        (o) => String(o.id) === String(this.selectedObligationId)
      );
  }

  assignSelected() {
    if (!this.canAssign() || !this.project || !this.project.id) {
      return;
    }

    const selected = this.getSelectedLines();
    const obligationId = this.selectedObligationId;
    this.saving = true;

    const requests = selected.map((line) =>
      this.$http.patch(
        `${this.$rootScope.apiHost}/api/admin/projects/${this.project.id}/funding/${line.id}`,
        { contractFundingObligationId: obligationId },
        { withCredentials: true }
      ).then(() => ({ line, ok: true }), (failure) => ({
        line,
        ok: false,
        message: (failure && failure.data && failure.data.message) || 'Failed to assign'
      }))
    );

    this.$q.all(requests).then((results) => {
      const succeeded = results.filter((r) => r.ok);
      const failed = results.filter((r) => !r.ok);

      succeeded.forEach((result) => {
        this.lines = this.lines.filter((line) => line.id !== result.line.id);
        delete this.selectedIds[result.line.id];
      });

      if (succeeded.length && !failed.length) {
        this.toastr.success(
          succeeded.length === 1
            ? 'Funding line assigned to obligation'
            : `${succeeded.length} funding lines assigned to obligation`
        );
      } else if (succeeded.length && failed.length) {
        this.toastr.warning(
          `${succeeded.length} assigned, ${failed.length} failed`,
          'Partial Success'
        );
      } else if (failed.length) {
        this.toastr.error(failed[0].message || 'Failed to assign funding lines', 'ERROR');
      }

      if (!this.lines.length) {
        this.$uibModalInstance.close({ assignedCount: succeeded.length });
      }
    }).finally(() => {
      this.saving = false;
    });
  }

  done() {
    this.$uibModalInstance.close({ assignedCount: 0, remaining: this.lines.length });
  }

  cancel() {
    this.$uibModalInstance.dismiss();
  }
}

export default UnassociatedFundingLinesModalController;



// WEBPACK FOOTER //
// ./client/common/modals/unassociatedFundingLinesModal/unassociatedFundingLinesModal.controller.js